/**
 * Compliance → Erasure (GDPR Art 17 / DPDPA §12).
 *
 * Operator submits a request (principal identifier + kind) → controller
 * hashes + snapshots in-scope assets → DPO approves → controller records
 * a tombstone-intent receipt per asset. Live DB-level erasure executes
 * when the customer-VPC scanner pod consumes the connector-side erasure
 * command on its next reconcile.
 */
import { useRegulationText } from '@/lib/regulation-terms';
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  ShieldCheck,
  Play,
  Eye,
  Ban,
  Trash2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listErasureJobs,
  createErasureRequest,
  approveErasureJob,
  executeErasureJob,
  type ErasureJob,
  type ErasureJobStatus,
  type ErasureIdentifierKind,
} from '@/lib/api';

const STATUS_VARIANT: Record<ErasureJobStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  approved: 'warning',
  running: 'warning',
  completed: 'success',
  failed: 'critical',
  cancelled: 'critical',
};

const STATUS_ICON: Record<ErasureJobStatus, typeof CheckCircle2> = {
  pending: Clock,
  approved: ShieldCheck,
  running: Loader2,
  completed: CheckCircle2,
  failed: XCircle,
  cancelled: Ban,
};

const IDENTIFIER_OPTIONS: ReadonlyArray<{ value: ErasureIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' },
  { value: 'custom', label: 'Custom identifier' },
];

export function ComplianceErasure(): JSX.Element {
  const tx = useRegulationText();
  const [jobs, setJobs] = useState<ErasureJob[]>([]);
  const [counts, setCounts] = useState({
    pending: 0, approved: 0, running: 0, completed: 0, failed: 0, cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Request form
  const [identifierKind, setIdentifierKind] = useState<ErasureIdentifierKind>('email');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [detailJob, setDetailJob] = useState<ErasureJob | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listErasureJobs({});
      setJobs(r.jobs);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load erasure jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSubmit(): Promise<void> {
    if (!principalIdentifier.trim()) {
      setError('Principal identifier is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createErasureRequest({
        principalIdentifier: principalIdentifier.trim(),
        identifierKind,
        ...(requestReason.trim() ? { requestReason: requestReason.trim() } : {}),
      });
      setPrincipalIdentifier('');
      setRequestReason('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to submit erasure request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove(j: ErasureJob): Promise<void> {
    const reason = window.prompt(`Approve erasure for principal ${j.principalIdentifierHash.slice(0, 12)}…?\nApproval reason:`);
    if (!reason) return;
    try {
      await approveErasureJob(j.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'approve failed');
    }
  }

  async function handleExecute(j: ErasureJob): Promise<void> {
    if (!window.confirm(
      `Execute erasure across ${j.assetsScope.length} asset(s)? ` +
      `Each asset will receive a tombstone intent recorded in the audit chain. ` +
      `This is non-reversible per the regulator audit.`,
    )) return;
    try {
      await executeErasureJob(j.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'execute failed');
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Right to erasure (GDPR Art 17 / DPDPA §12)"
        subtitle={tx("Submit, approve, and execute erasure requests. Principal identifier is hashed at submit-time; raw PII never reaches the audit chain.")}
        badges={[
          { label: 'DPO-gated', variant: 'glass' },
          { label: 'SHA-256 principal hash', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Erasure action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Pending" value={counts.pending} icon={Clock} glow={counts.pending > 0 ? 'medium' : 'low'} hint="awaiting approval" />
        <KpiCard label="Approved" value={counts.approved} icon={ShieldCheck} glow={counts.approved > 0 ? 'medium' : 'low'} hint="ready to execute" />
        <KpiCard label="Completed" value={counts.completed} icon={CheckCircle2} glow={counts.completed > 0 ? 'high' : 'low'} hint="tombstoned" />
        <KpiCard label="Failed" value={counts.failed} icon={XCircle} glow={counts.failed > 0 ? 'critical' : 'low'} hint="see execution log" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Submit request */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Submit erasure request
            </CardTitle>
            <CardDescription>
              The identifier is SHA-256 hashed before persistence; the raw value never leaves
              this form. The controller snapshots which assets touch the principal via the
              access-graph walk so a DPO can review scope before approving.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Identifier kind</label>
              <select
                value={identifierKind}
                onChange={(e) => setIdentifierKind(e.target.value as ErasureIdentifierKind)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {IDENTIFIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input
                type="text"
                value={principalIdentifier}
                onChange={(e) => setPrincipalIdentifier(e.target.value)}
                placeholder={identifierKind === 'aadhaar' ? '12-digit Aadhaar' : identifierKind === 'email' ? 'alice@example.com' : 'value'}
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Hashed on submit; never persisted in raw form.
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Principal exercised Art 17 right via webform"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_erasure.a01" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                Submit request
              </Button>
            </div>
          </div>
        </GlassCard>

        {/* Queue */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Erasure queue</CardTitle>
            <CardDescription>
              Workflow: <strong>pending → approved → running → completed</strong>. Each
              transition is audit-chain logged.
            </CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && jobs.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Trash2 className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No erasure jobs yet.</div>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j, i) => {
                const SIcon = STATUS_ICON[j.status];
                return (
                  <motion.div
                    key={j.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{j.identifierKind}</Badge>
                          <Badge variant={STATUS_VARIANT[j.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className={`h-3 w-3 ${j.status === 'running' ? 'animate-spin' : ''}`} />
                            {j.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            scope: {j.assetsScope.length} asset(s)
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={j.principalIdentifierHash}>
                          {j.principalIdentifierHash.slice(0, 24)}…
                        </div>
                        {j.requestReason && (
                          <div className="text-[10px] mt-1 text-muted-foreground">
                            {j.requestReason}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => setDetailJob(j)} title="Details">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {j.status === 'pending' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_erasure.a02" onClick={() => void handleApprove(j)} title="Approve">
                            <ShieldCheck className="h-3 w-3" />
                          </Button>
                        )}
                        {j.status === 'approved' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_erasure.a03" onClick={() => void handleExecute(j)} title="Execute">
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Detail modal */}
      <Dialog open={detailJob !== null} onOpenChange={(o) => { if (!o) setDetailJob(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Erasure job detail</DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Status</div>
                  <Badge variant={STATUS_VARIANT[detailJob.status]} className="text-[10px]">{detailJob.status}</Badge>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Identifier kind</div>
                  <code className="font-mono">{detailJob.identifierKind}</code>
                </div>
                <div className="glass-lite rounded p-2 col-span-2">
                  <div className="text-muted-foreground mb-0.5">Principal hash</div>
                  <code className="font-mono text-[10px] break-all">{detailJob.principalIdentifierHash}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Created</div>
                  <code className="font-mono">{new Date(detailJob.createdAt).toLocaleString()}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Completed</div>
                  <code className="font-mono">{detailJob.completedAt ? new Date(detailJob.completedAt).toLocaleString() : '-'}</code>
                </div>
              </div>

              {detailJob.approverActorId && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1">Approval</div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.approverActorId.slice(0, 8)}…</code>
                    {detailJob.approverDecidedAt && <> at {new Date(detailJob.approverDecidedAt).toLocaleString()}</>}
                  </div>
                  {detailJob.approverReason && (
                    <div className="mt-1">Reason: <em>{detailJob.approverReason}</em></div>
                  )}
                </div>
              )}

              <div>
                <div className="font-medium mb-1">Assets in scope ({detailJob.assetsScope.length})</div>
                {detailJob.assetsScope.length === 0 ? (
                  <div className="glass-lite rounded p-3 text-muted-foreground italic">
                    The access-graph walk found no assets touching this principal.
                  </div>
                ) : (
                  <div className="glass-lite rounded p-2 max-h-32 overflow-y-auto">
                    {detailJob.assetsScope.map((id) => (
                      <code key={id} className="block font-mono text-[10px]">{id}</code>
                    ))}
                  </div>
                )}
              </div>

              {detailJob.executionLog.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Execution log</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailJob.executionLog.map((e, i) => (
                      <div key={i} className="glass-lite rounded p-2 flex items-start gap-2">
                        <Badge
                          variant={e.status === 'ok' ? 'success' : e.status === 'failed' ? 'critical' : 'warning'}
                          className="text-[9px] shrink-0"
                        >
                          {e.status}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-muted-foreground">{e.assetId}</div>
                          <div>{e.detail}</div>
                          <div className="text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()} · {e.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailJob.errorDetail && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Error</div>
                  <div>{detailJob.errorDetail}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
